import React, { useCallback, useRef, useState } from 'react';
import { Outlet, useParams } from 'react-router';
import AppLayoutToolbar from '@amzn/awsui-components-console/app-layout-toolbar';
import BreadcrumbGroup from '@amzn/awsui-components-console/breadcrumb-group';
import Link from '@amzn/awsui-components-console/link';
import AppHeader from 'src/common/ui/AppHeader';
import AppNavContent from 'src/common/ui/AppNavContent';
import NamespaceSideNav from 'src/common/ui/NamespaceSideNav';
import FooterBar from 'src/common/ui/FooterBar';
import SkipLink from 'src/common/ui/SkipLink';
import ChangeControlBanner from 'src/common/ui/ChangeControlBanner';
import { useResizable } from 'src/common/hooks/useResizable';
import { useClickOutside } from 'src/common/hooks/useClickOutside';
import { useEscapeKey } from 'src/common/hooks/useEscapeKey';
import { useRouteAnnouncement } from 'src/common/hooks/useRouteAnnouncement';
import { MAIN_CONTENT_ID, IS_IT_DOWN_URL } from 'src/common/constants';
import { useTranslation } from 'src/i18n/shim/useTranslation';
import styles from './AppShell.module.scss';

const NAV_WIDTH_MIN = 240;
const NAV_WIDTH_MAX = 480;
const NAV_WIDTH_DEFAULT = NAV_WIDTH_MIN;

/**
 * Persistent application shell rendered as a React Router layout route.
 * Stays mounted across all child route navigations, preserving navigation
 * open/close state and avoiding chrome flicker.
 *
 * Child page content is rendered via <Outlet />.
 */
const AppShell: React.FC = () => {
  const { t } = useTranslation();
  const { namespaceId } = useParams<{ namespaceId: string }>();
  const [navigationOpen, setNavigationOpen] = useState(false);
  const navWrapperRef = useRef<HTMLDivElement>(null);

  const closeNavigation = useCallback(() => setNavigationOpen(false), []);

  const navAriaLabels = {
    navigation: t('shell-nav-aria'),
    navigationClose: t('shell-nav-close-aria'),
    navigationToggle: t('shell-nav-toggle-aria'),
  };

  /** Derived from `navAriaLabels` rather than listed again: `useClickOutside` compares
   * `aria-label` by exact string, so a separate English list would stop matching — and the drawer
   * would close on its own toggle — in every other locale. */
  const navIgnoreAriaLabels: readonly string[] = Object.values(navAriaLabels);

  useClickOutside({
    enabled: navigationOpen,
    ref: navWrapperRef,
    ignoreAriaLabels: navIgnoreAriaLabels,
    onOutsideClick: closeNavigation,
  });

  // The drawer's close button is hidden by AppShell.module.scss, so without
  // Escape a keyboard-only user cannot close it.
  useEscapeKey({ enabled: navigationOpen, onEscape: closeNavigation });

  const { width: navigationWidth, handleProps } = useResizable({
    initialWidth: NAV_WIDTH_DEFAULT,
    minWidth: NAV_WIDTH_MIN,
    maxWidth: NAV_WIDTH_MAX,
  });

  // Route-aware document title + the label announced to screen readers on each
  // client-side navigation (see useRouteAnnouncement).
  const routeAnnouncement = useRouteAnnouncement();

  const navigationContent = (
    <div className={styles.navWrapper} ref={navWrapperRef}>
      <NamespaceSideNav activeNamespace={namespaceId} />
      <div
        className={styles.resizeHandle}
        {...handleProps}
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={navigationWidth}
        aria-valuemin={NAV_WIDTH_MIN}
        aria-valuemax={NAV_WIDTH_MAX}
        aria-label={t('shell-resize-nav-aria')}
        tabIndex={0}
      />
    </div>
  );

  return (
    <>
      <SkipLink targetId={MAIN_CONTENT_ID} />
      <AppHeader navContent={<AppNavContent />} />
      <AppLayoutToolbar
        contentType="default"
        toolsHide={true}
        headerSelector=".langhub-app-header"
        navigationWidth={navigationWidth}
        navigationOpen={navigationOpen}
        onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
        navigation={navigationContent}
        ariaLabels={navAriaLabels}
        breadcrumbs={
          /* The breadcrumb line is the "My Namespaces" row. The status link is
             pushed to the far right of the same line via flex space-between so
             it sits opposite the breadcrumb rather than inside the nav drawer. */
          <div className={styles.breadcrumbRow}>
            <BreadcrumbGroup
              items={[{ text: t('shell-breadcrumb-my-namespaces'), href: '#' }]}
            />
            <span className={styles.statusLink}>
              <Link
                href={IS_IT_DOWN_URL}
                external
                target="_blank"
                data-testid="is-langhub-down-link"
              >
                {t('shell-status-link')}
              </Link>
            </span>
          </div>
        }
        // Asked once per page load here, in the shell, so every route shows the
        // change-control banner without re-querying; renders nothing on a normal day.
        notifications={<ChangeControlBanner />}
        // Skip-link target. tabIndex={-1} lets activating the link move keyboard
        // focus here (not just scroll), landing the user past the chrome. Not a
        // <main>: AppLayoutToolbar already renders the main landmark, and a
        // second one would split it.
        content={
          <div
            id={MAIN_CONTENT_ID}
            tabIndex={-1}
            className={styles.mainContent}
          >
            <Outlet />
          </div>
        }
      />
      <FooterBar />
      {/* Polite live region: screen readers announce the page name after each
          SPA navigation. Persistent (AppShell outlives route changes) so the
          text change is what triggers the announcement. */}
      <div className={styles.srOnly} aria-live="polite" aria-atomic="true">
        {routeAnnouncement}
      </div>
    </>
  );
};

export default AppShell;
